import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → Access Control page (full-admin only).
 *
 * Lets a full admin grant a specific non-admin user access to ONE
 * individual admin tool by email, without making them a full admin — see
 * server/services/toolAccessStore.js for exactly what a granted user can
 * and can't do. Currently piloted on the Salesforce API
 * (server/routes/toolAccess.js is the backend).
 */

function fmtDate(iso) {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function AdminAccessControl({ embedded = false } = {}) {
    const { oktaAuth } = useOktaAuth() || {};
    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newEmail, setNewEmail] = useState({}); // { [toolId]: string }
    const [busyTool, setBusyTool] = useState('');

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const headers = await authHeader();
            const res = await fetch('/api/admin/tool-access', { headers });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setTools(body.tools || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [authHeader]);

    useEffect(() => { load(); }, [load]);

    const grant = useCallback(async (toolId) => {
        const email = (newEmail[toolId] || '').trim();
        if (!email) return;
        setBusyTool(toolId);
        setError('');
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch(`/api/admin/tool-access/${encodeURIComponent(toolId)}`, {
                method: 'POST', headers, body: JSON.stringify({ email }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            setNewEmail((prev) => ({ ...prev, [toolId]: '' }));
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusyTool('');
        }
    }, [authHeader, newEmail, load]);

    const revoke = useCallback(async (toolId, email) => {
        setBusyTool(toolId);
        setError('');
        try {
            const headers = await authHeader();
            const res = await fetch(`/api/admin/tool-access/${encodeURIComponent(toolId)}/${encodeURIComponent(email)}`, {
                method: 'DELETE', headers,
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
            await load();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusyTool('');
        }
    }, [authHeader, load]);

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>🔐 Access Control</h2>
            </div>
            <p style={styles.subtitle}>
                Grant a specific person access to ONE admin tool by email, without making them a
                full admin. A granted user gets full use of that tool, but can never manage anyone
                else&apos;s access (including their own) or remove/delete anything within it —
                those actions stay full-admin only.
            </p>

            {!embedded && <AdminMenu />}

            {error && <div style={styles.error}>{error}</div>}
            {loading ? <p style={styles.help}>Loading…</p> : tools.map((tool) => (
                <div key={tool.id} style={styles.section}>
                    <h3 style={styles.h3}>{tool.label}</h3>
                    <p style={styles.help}>{tool.description}</p>

                    {tool.users.length === 0 ? (
                        <p style={styles.help}><i>No one has been granted delegated access yet.</i></p>
                    ) : (
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Email</th>
                                    <th style={styles.th}>Added</th>
                                    <th style={styles.th}>Added by</th>
                                    <th style={styles.th} />
                                </tr>
                            </thead>
                            <tbody>
                                {tool.users.map((u) => (
                                    <tr key={u.email}>
                                        <td style={styles.td}>{u.email}</td>
                                        <td style={styles.td}>{fmtDate(u.addedAt)}</td>
                                        <td style={styles.td}>{u.addedBy || '—'}</td>
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                style={styles.btnDanger}
                                                disabled={busyTool === tool.id}
                                                onClick={() => revoke(tool.id, u.email)}
                                            >
                                                Revoke
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <div style={styles.actions}>
                        <input
                            style={styles.input}
                            type="email"
                            placeholder="someone@ischemaview.com"
                            value={newEmail[tool.id] || ''}
                            onChange={(e) => setNewEmail((prev) => ({ ...prev, [tool.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') grant(tool.id); }}
                        />
                        <button
                            type="button"
                            style={styles.btnPrimary}
                            disabled={busyTool === tool.id || !(newEmail[tool.id] || '').trim()}
                            onClick={() => grant(tool.id)}
                        >
                            {busyTool === tool.id ? 'Granting…' : '+ Grant access'}
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    h3: { margin: '0 0 4px', color: '#e0e0e0' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: 0 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
    th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #2f3a44', color: '#aaa', fontWeight: 600, fontSize: '0.85rem' },
    td: { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #232a31' },
    input: {
        padding: '8px 10px', borderRadius: '4px', border: '1px solid #3a4550', background: '#161a1e', color: '#fff', minWidth: '260px',
    },
    btnPrimary: { padding: '8px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnDanger: { padding: '6px 10px', background: '#5a1a1a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
    actions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px', flexWrap: 'wrap' },
    error: { padding: '8px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
};
